/**
 * Pure helpers for the /draft flow.
 *
 * SRP: pure data transforms. No React, no Office.js, no fetches.
 * Every branch is unit-testable with plain input literals.
 *
 * # HISTORY: Spec 29 Phase A (2026-04-23) introduced this module
 * #   for the CONVERSATIONAL /draft chip picker — Asthra asked the
 * #   writer "what are you drafting?" then "which library?", and
 * #   the writer answered via inline chips. Phase 6 (2026-04-26)
 * #   retired chip-picker selection per the rule "library +
 * #   doctype each have ONE canonical surface" (LibraryHub for
 * #   library, DoctypePicker for doctype). The chip-builder /
 * #   prompt-copy helpers were retired with that change; only the
 * #   selection-synthesis helper survives because it's still the
 * #   shell's hand-off point from picker to run.
 *
 * What lives here now:
 *   - ``DoctypeChoice``: typed selection payload the picker emits.
 *   - ``AskingLibraryContext`` + ``buildLoomFreshSelection``: the
 *     pure synthesis from (doctype, library, workspace) into the
 *     LoomFreshSelection the run pipeline accepts.
 */

import type { DocDomain } from "./doc-catalog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Exactly what the shell needs to construct a ``LoomFreshSelection``
 *  once both picks land. Narrow on purpose — YAGNI. */
export interface DoctypeChoice {
  docCode: string;
  backendDocType: string;
  docFramework: string | null;
  docName: string;
  domain: DocDomain;
  /** Optional regulatory-authority chip (e.g. "ICH", "MDR"). */
  auth: string | null;
}

// ---------------------------------------------------------------------------
// Selection synthesis — pure
// ---------------------------------------------------------------------------

/** Subset of the asking-library state shape needed to synthesise a
 *  LoomFreshSelection. Decoupled from the shell's full DraftFlowStep
 *  union so this helper stays pure + portable. */
export interface AskingLibraryContext {
  docCode: string;
  backendDocType: string;
  docFramework: string | null;
  docName: string;
}

/** Inputs to the LoomFreshSelection synthesis: the doctype context
 *  the writer already picked, the library they just selected, and
 *  the active workspace. Pure: same inputs → same output, no I/O. */
export function buildLoomFreshSelection(
  doctype: AskingLibraryContext,
  library: { id: string; name: string },
  workspaceId: string | null,
): {
  workspaceId: string | null;
  libraryId: string;
  libraryName: string;
  docCode: string;
  docName: string;
  docFramework?: string;
  backendDocType: string;
  folderId?: string | null;
} {
  return {
    workspaceId,
    libraryId: library.id,
    libraryName: library.name,
    docCode: doctype.docCode,
    docName: doctype.docName,
    docFramework: doctype.docFramework ?? undefined,
    backendDocType: doctype.backendDocType,
    folderId: null,
  };
}

/** Build the committed selection for a custom (build-on-existing-doc) run whose
 *  writer SKIPPED the source library. The library fields are intentionally
 *  empty — the audit library is later bootstrapped from the document's own
 *  content — while the doctype fields keep the doctype chip resolved. Pure: the
 *  shell hands the already-resolved doc name/framework in. ``docCode`` doubles
 *  as ``backendDocType`` because a custom import carries the backend stem in
 *  ``RunLoadContext.docType``. */
export function buildCustomSkipSelection(args: {
  docCode: string;
  docName: string;
  docFramework?: string | null;
  workspaceId: string | null;
}): {
  workspaceId: string | null;
  libraryId: string;
  libraryName: string;
  docCode: string;
  docName: string;
  docFramework?: string;
  backendDocType: string;
} {
  return {
    workspaceId: args.workspaceId,
    libraryId: "",
    libraryName: "",
    docCode: args.docCode,
    docName: args.docName,
    docFramework: args.docFramework ?? undefined,
    backendDocType: args.docCode,
  };
}
